import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, admin } from '../lib/api.js';
import { formatDate } from '../lib/format.js';
import type { OrgUser, OrgUsersResult } from '../lib/types.js';
import { RulesDialog } from './RulesDialog.jsx';
import './OrgUsers.css';

export function OrgUsers({ clientId, onClose }: { clientId: number; onClose: () => void }) {
  const [regoleDi, setRegoleDi] = useState<OrgUser | null>(null);

  const data = useQuery<OrgUsersResult>({
    queryKey: ['admin', 'clients', clientId, 'users'],
    queryFn: () => admin.clientUsers(clientId),
  });

  return (
    <div className="picker" role="dialog" aria-modal="true" aria-label="Utenti">
      <div className="picker__box card orgusers">
        <header className="picker__head">
          <h3>Utenti di {data.data?.client.displayName ?? '…'}</h3>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            Chiudi
          </button>
        </header>

        <div className="orgusers__body">
          {data.isPending ? (
            <p className="orgusers__state">Caricamento…</p>
          ) : data.isError ? (
            <div className="alert alert--error">
              {data.error instanceof ApiError ? data.error.message : 'Caricamento non riuscito.'}
            </div>
          ) : (
            <>
              {data.data.client.atlantePrefix ? (
                <p className="orgusers__note">
                  Gli utenti con prefisso <code>{data.data.client.atlantePrefix}</code> vengono
                  abilitate al primo accesso: qui compaiono solo quelle che hanno già effettuato
                  almeno un tentativo di login.
                </p>
              ) : (
                <p className="orgusers__note orgusers__note--warn">
                  Nessun prefisso mappato: gli utenti vanno associati a mano.
                </p>
              )}

              {data.data.users.length === 0 ? (
                <p className="orgusers__state">Nessuna utenza associata.</p>
              ) : (
                <div className="orgusers__scroll">
                  <table className="orgusers__table">
                    <thead>
                      <tr>
                        <th>Utenza</th>
                        <th>Origine</th>
                        <th>Ultimo accesso</th>
                        <th>Stato</th>
                        <th>
                          <span className="visually-hidden">Azioni</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.data.users.map((u) => (
                        <tr key={u.id}>
                          <td>
                            <strong>{u.username}</strong>
                            {u.displayName ? (
                              <span className="orgusers__sub">{u.displayName}</span>
                            ) : null}
                            {u.isInternal ? (
                              <span className="orgusers__tag">interno</span>
                            ) : null}
                            {u.isAdmin ? <span className="orgusers__tag">admin</span> : null}
                          </td>
                          <td>
                            {u.origine === 'esplicita' ? (
                              <span className="orgusers__muted">associazione</span>
                            ) : (
                              <span
                                className="orgusers__warn"
                                title="Ha il prefisso ma non l'associazione: rientrerebbe al primo accesso"
                              >
                                solo prefisso
                              </span>
                            )}
                          </td>
                          <td className="orgusers__muted">
                            {u.maiAccesso ? (
                              <span className="orgusers__never">mai</span>
                            ) : (
                              formatDate(u.lastLoginAt)
                            )}
                          </td>
                          <td>
                            {u.isActive ? (
                              <span className="admin__badge admin__badge--on">attiva</span>
                            ) : (
                              <span className="admin__badge">disattivata</span>
                            )}
                          </td>
                          <td className="orgusers__actions">
                            {/*
                              Le regole personali valgono per questa utenza
                              dentro questa organizzazione: si scrivono da qui,
                              dove si vede di chi si sta parlando.
                            */}
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              onClick={() => setRegoleDi(u)}
                            >
                              Regole
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <p className="orgusers__footnote">
                Inoltre {data.data.utentiInterni} utenz{data.data.utentiInterni === 1 ? 'a' : 'e'}{' '}
                intern{data.data.utentiInterni === 1 ? 'a' : 'e'} (staff SIAP) accede a tutte le
                organizzazioni senza associazione, e non compare in questo elenco.
              </p>
            </>
          )}
        </div>
      </div>

      {regoleDi && data.data ? (
        <RulesDialog
          target={{
            scope: 'user',
            clientId: data.data.client.id,
            userId: regoleDi.id,
            label: `${regoleDi.username} · ${data.data.client.displayName}`,
          }}
          onClose={() => setRegoleDi(null)}
        />
      ) : null}
    </div>
  );
}
